// Layout tab — the visual type's drop zones (its ColumnGroups, as defined in
// the visual catalog), shown full (collapsible zones with their fields) or
// minimised (an icon tile per zone with a count — still a drop target). Hidden
// is the parent's business: it simply does not render this.
//
// A zone's icon, name, accepted type and field limit all come from the visual
// definition. Fields are dragged in from the tree, between zones, or reordered
// inside a zone (drop on a field = insert before it).

import { useEffect, useRef, useState, type ReactNode } from 'react'
import { ChevronDown, ChevronRight, GripVertical, Maximize2, Minimize2, X } from 'lucide-react'
import type { ColumnGroup, VisualColumn } from './types'
import { SlotIcon } from './slotIcons'

export type ZoneDrop = { ok: boolean; reason?: string }

export interface DropZonesProps {
  groups: ColumnGroup[]
  iconOf: (g: ColumnGroup) => string
  mode: 'full' | 'mini'
  typeName: string
  readOnly: boolean
  dragging: boolean
  canDrop: (std: string) => ZoneDrop
  selectedColId: number | null
  colLabel: (c: VisualColumn) => string
  colGlyph: (c: VisualColumn) => { text: string; cls: string }
  colAgg: (c: VisualColumn) => string
  onDrop: (std: string, beforeColId?: number) => void
  onSelect: (colId: number | null) => void
  onRemove: (colId: number) => void
  onDragStartCol: (colId: number) => void
  onDragEnd: () => void
  renderEditor: (colId: number) => ReactNode
  setMode: (m: 'full' | 'mini' | 'hidden') => void
  footer?: ReactNode
}

const limitOf = (g: ColumnGroup) => Number(g.MaxColumns || 0)
/** Below this width the full zones become icon tiles on their own. */
const FULL_MIN_WIDTH = 250
/** The zone's description from the catalog — '' for the unfilled
 *  "GroupDescription1"-style placeholders many catalog entries still carry. */
export const groupHint = (g: ColumnGroup): string => {
  const d = (g.GroupDescription || '').trim()
  return /^GroupDescription\d*$/i.test(d) ? '' : d
}
const accWord = (g: ColumnGroup) => {
  const t = (g.AllowedColumnTypes || 'ANY').toUpperCase()
  return t === 'NUMBER' ? 'Numbers only' : t === 'DATE' ? 'Dates only' : t === 'STRING' ? 'Text only' : ''
}

/** Hierarchy levels: show the ones in use plus the next one, not all ten. */
export function visibleGroups(groups: ColumnGroup[]): ColumnGroup[] {
  const lv = (g: ColumnGroup) => {
    const m = /^HierarchyLevel(\d+)$/i.exec(g.StandardName || '')
    return m ? Number(m[1]) : 0
  }
  const used = groups.filter((g) => lv(g) && (g.COLUMNS || []).length).map(lv)
  const top = Math.max(2, (used.length ? Math.max(...used) : 0) + 1)
  return groups.filter((g) => !lv(g) || lv(g) <= top)
}

export default function DropZones(p: DropZonesProps) {
  const [closed, setClosed] = useState<Record<string, boolean>>({})
  const [hot, setHot] = useState<string | null>(null)
  const [before, setBefore] = useState<number | null>(null)
  const groups = visibleGroups(p.groups)
  const hiddenLevels = p.groups.length - groups.length
  // Too narrow for the full zones: show the icon tiles instead — without
  // touching the author's saved choice, so widening the panel brings the full
  // zones back by themselves.
  const rootRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(0)
  useEffect(() => {
    // The CONTAINER's width (the same in both views) — measuring this pane
    // itself would flip back and forth near the threshold, its padding
    // differing between the full and tile views.
    const el = rootRef.current?.parentElement
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(([e]) => setWidth(Math.round(e.contentRect.width)))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  const autoMini = p.mode === 'full' && width > 0 && width < FULL_MIN_WIDTH
  const mode = autoMini ? 'mini' : p.mode

  const zoneCls = (g: ColumnGroup, base: string) => {
    const n = (g.COLUMNS || []).length
    const d = p.dragging ? p.canDrop(g.StandardName) : null
    return [
      base,
      n ? 'filled' : '',
      d ? (d.ok ? 'ok' : 'no') : '',
      hot === g.StandardName ? 'hot' : '',
      Number(g.MinColumns || 0) >= 1 && !n ? 'req' : '',
    ].filter(Boolean).join(' ')
  }
  const over = (g: ColumnGroup) => (e: React.DragEvent) => {
    if (!p.dragging || p.readOnly) return
    if (!p.canDrop(g.StandardName).ok) return
    e.preventDefault()
    if (hot !== g.StandardName) setHot(g.StandardName)
  }
  const drop = (g: ColumnGroup, beforeColId?: number) => (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation()
    setHot(null); setBefore(null)
    if (p.readOnly || !p.canDrop(g.StandardName).ok) return
    p.onDrop(g.StandardName, beforeColId)
  }

  if (mode === 'mini') {
    return (
      <div ref={rootRef} className="vdo-mini" aria-label="Drop zones, minimised">
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 2px 8px' }}>
          <button className="vdo-ib sm" aria-label="Expand the drop zones" disabled={autoMini}
            title={autoMini ? 'Widen the panel (drag its edge) to see the zones in full' : 'Expand'}
            onClick={() => p.setMode('full')}><Maximize2 size={14} /></button>
          <button className="vdo-ib sm" aria-label="Hide the drop zones" title="Hide" onClick={() => p.setMode('hidden')}><X size={14} /></button>
        </div>
        <div className="vdo-mgrid">
          {groups.map((g) => {
            const cols = g.COLUMNS || []
            return (
              <button
                key={g.StandardName}
                className={zoneCls(g, 'vdo-mtile')}
                title={`${g.GroupName}${cols.length ? ': ' + cols.map(p.colLabel).join(', ') : groupHint(g) ? ' — ' + groupHint(g) : ''} · click to open`}
                onClick={() => { setClosed((c) => ({ ...c, [g.StandardName]: false })); p.setMode('full') }}
                onDragOver={over(g)}
                onDragLeave={() => setHot((h) => (h === g.StandardName ? null : h))}
                onDrop={drop(g)}
              >
                <SlotIcon token={p.iconOf(g)} size={20} />
                <span className="nm">{g.GroupName}</span>
                {cols.length > 0 && <span className="c">{cols.length}</span>}
              </button>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div ref={rootRef} style={{ padding: '10px 14px 18px 12px' }}>
      <div className="vdo-colh">
        <span className="vdo-wi" style={{ width: 24, height: 24 }}><SlotIcon token="columns" size={14} /></span>
        Drop zones <span className="s">· {p.typeName}</span>
        <span style={{ flex: 1 }} />
        <button className="vdo-ib sm" aria-label="Minimise the drop zones" title="Minimise to icons" onClick={() => p.setMode('mini')}><Minimize2 size={14} /></button>
        <button className="vdo-ib sm" aria-label="Hide the drop zones" title="Hide" onClick={() => p.setMode('hidden')}><X size={14} /></button>
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--color-text-faint)', padding: '0 4px 2px' }}>
        Drag fields here or straight onto the chart.
      </div>
      {groups.map((g) => {
        const cols = g.COLUMNS || []
        const open = !closed[g.StandardName]
        const limit = limitOf(g)
        const d = p.dragging ? p.canDrop(g.StandardName) : null
        return (
          <div
            key={g.StandardName}
            className={zoneCls(g, 'vdo-well')}
            onDragOver={over(g)}
            onDragLeave={(e) => { if (e.currentTarget === e.target) setHot((h) => (h === g.StandardName ? null : h)) }}
            onDrop={drop(g)}
          >
            <button className="vdo-well-h" aria-expanded={open} title={groupHint(g) || g.GroupName}
              onClick={() => setClosed((c) => ({ ...c, [g.StandardName]: open }))}>
              <span className="vdo-wi"><SlotIcon token={p.iconOf(g)} size={16} /></span>
              <span className="vdo-well-t">{g.GroupName}</span>
              {Number(g.MinColumns || 0) >= 1 && !cols.length && <span className="vdo-badge req">Required</span>}
              {accWord(g) && <span className="vdo-badge acc">{accWord(g)}</span>}
              <span className="vdo-well-s">
                {open ? (limit ? `${cols.length} of ${limit}` : cols.length ? String(cols.length) : '')
                  : (cols.map(p.colLabel).join(', ') || 'empty')}
              </span>
              {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            </button>
            {open && (
              <div className="vdo-well-b">
                {cols.map((c) => {
                  const gl = p.colGlyph(c)
                  const agg = p.colAgg(c)
                  const sel = p.selectedColId === c.COLID
                  return (
                    <div key={c.COLID}>
                      <div
                        className={`vdo-chip${sel ? ' sel' : ''}${before === c.COLID ? ' before' : ''}`}
                        draggable={!p.readOnly}
                        onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(c.COLID)); p.onDragStartCol(c.COLID) }}
                        onDragEnd={() => { setBefore(null); p.onDragEnd() }}
                        onDragOver={(e) => {
                          if (!p.dragging || p.readOnly || !p.canDrop(g.StandardName).ok) return
                          e.preventDefault(); e.stopPropagation()
                          if (before !== c.COLID) setBefore(c.COLID)
                        }}
                        onDragLeave={() => setBefore((b) => (b === c.COLID ? null : b))}
                        onDrop={drop(g, c.COLID)}
                        title="Drag to reorder or move · drag back to Fields to remove"
                      >
                        <GripVertical size={12} style={{ opacity: 0.45, flexShrink: 0 }} />
                        <button className="vdo-chip-m" onClick={() => p.onSelect(sel ? null : c.COLID)} aria-expanded={sel}>
                          <span className={`vdo-glyph ${gl.cls}`}>{gl.text}</span>
                          <span className="lab">{p.colLabel(c)}</span>
                          {agg && <span className="vdo-agg">{agg}</span>}
                        </button>
                        {!p.readOnly && (
                          <button className="vdo-ib sm" aria-label={`Remove ${p.colLabel(c)}`} title="Remove from the visual"
                            onClick={() => p.onRemove(c.COLID)}><X size={12} /></button>
                        )}
                      </div>
                      {sel && <div className="vdo-editor" style={{ marginTop: 6 }}>{p.renderEditor(c.COLID)}</div>}
                    </div>
                  )
                })}
                {!cols.length && (
                  <div className="vdo-dropz">{d && !d.ok ? (d.reason || accWord(g) || 'Not here') : (groupHint(g) || 'Drop a field here')}</div>
                )}
              </div>
            )}
          </div>
        )
      })}
      {hiddenLevels > 0 && (
        <div style={{ fontSize: 12.5, color: 'var(--color-text-faint)', padding: '8px 4px 0' }}>
          {hiddenLevels} more levels appear as you fill these.
        </div>
      )}
      {p.footer}
    </div>
  )
}
